import { Page } from '@app/components/page/Page';
import { PageSection } from '@app/core/components/PageSection';
import { pushTo } from '@app/core/routing';
import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import { SocialExportContext } from '@app/modules/social-export/components/dialogs/social-export-context';
import { PublishConnectionSelector } from '@app/modules/social-export/components/PublishConnectionSelector';
import { SocialPublishFormActions } from '@app/modules/social-export/components/SocialPublishFormActions';
import { isSamePlatformId } from '@app/modules/social-export/domain/is-same-platform-id';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { useAvailableSocialExportPlatformsForStation } from '@app/modules/social-export/hooks/useAvailableSocialExportPlatformsForStation';
import { useMediaLimitsIndication } from '@app/modules/social-export/hooks/useMediaLimitsIndication';
import { compositionToSocialExportPublishSource } from '@app/modules/social-export/lib/social-export-publish-source';
import {
  MediaLimitsAlert,
  MediaSelector,
  NoSourcesAlert,
  PublishPlatformBar,
  SocialExportUploadLayout,
  VideoPreview,
  publishMessages,
} from '@app/modules/social-export/publish';
import { webApi } from '@mi-front/api-clients';
import { SocialExportGetStationConnectionStateDto } from '@mi-front/api-v2';
import ShareIcon from '@mui/icons-material/Share';
import { Box, CircularProgress, Typography } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ComposeSocialExportUploadBreadcrumbs } from './ComposeSocialExportUploadBreadcrumbs';
import { type ComposeSocialExportPublishForm } from './useComposeSocialExportUploadPage';

export interface ComposeSocialExportUploadPageContentProps {
  composition: webApi.EnrichedCompositionDetailDto;
  /** Connection settings for the selected account; loading only affects the publish form. */
  settingsQuery: UseQueryResult<unknown, unknown>;
  platform: SocialPlatform;
  selectedSource: string;
  onSelectedSourceChange: (value: string) => void;
  edits: webApi.EnrichedVideoEditorEditListItemDto[];
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  hasSelectedRenderedEdit: boolean;
  renderedVideoUrl: string | null;
  PublishForm: ComposeSocialExportPublishForm;
  onUpload: (data: Record<string, unknown>) => Promise<void>;
  connections: SocialExportGetStationConnectionStateDto[];
  selectedConnectionId: string | null;
  onSelectedConnectionChange: (id: string | null) => void;
}

/** Layout and conditional UI once composition and edits are loaded (render may still be pending). */
export function ComposeSocialExportUploadPageContent({
  composition,
  settingsQuery,
  platform,
  selectedSource,
  onSelectedSourceChange,
  edits,
  hasAvailableSources,
  showMediaSelector,
  hasSelectedRenderedEdit,
  renderedVideoUrl,
  PublishForm,
  onUpload,
  connections,
  selectedConnectionId,
  onSelectedConnectionChange,
}: ComposeSocialExportUploadPageContentProps) {
  const { $t } = useIntl();
  const publishSource = compositionToSocialExportPublishSource(composition);
  const { platforms: availablePlatforms } = useAvailableSocialExportPlatformsForStation(
    composition.stationId ?? undefined,
  );

  const effectiveMediaUrl = renderedVideoUrl;
  const canPublish = effectiveMediaUrl !== null && hasSelectedRenderedEdit;

  // Fixed title: the platform is a selection inside the page, not the page identity.
  const title = $t(publishMessages.publishToSocialNetworks);
  const areSettingsReady = !settingsQuery.isLoading && settingsQuery.data !== undefined;

  const selectedConnection = useMemo(
    () => connections.find((c) => c.id === selectedConnectionId),
    [connections, selectedConnectionId],
  );
  // Composition edit duration is not on list DTOs; soft display tips still apply via station tier.
  const mediaLimitsIndication = useMediaLimitsIndication(selectedConnection, undefined);

  const handlePlatformSwitch = (platformId: string) => {
    if (isSamePlatformId(platformId, platform.id)) {
      return;
    }
    const editId = selectedSource || undefined;
    pushTo(composeRouteGenerator.upload(composition.id, platformId, editId), true);
  };

  const platformSwitcher = (
    <PageSection message={publishMessages.platformSwitcherLabel}>
      <PublishPlatformBar
        platforms={availablePlatforms}
        selectedPlatformId={platform.id}
        onSelectPlatform={handlePlatformSwitch}
      />
    </PageSection>
  );

  return (
    <SocialExportContext.Provider value={{ mediaUrl: effectiveMediaUrl ?? '' }}>
      <Page htmlTitle={title}>
        <Page.Header
          icon={<ShareIcon />}
          title={title}
          breadcrumbs={<ComposeSocialExportUploadBreadcrumbs composition={composition} />}
        />
        <Page.Content maxWidth="lg">
          {!composition.stationId ? (
            <Box sx={{ mt: 2 }}>{$t(publishMessages.noStation)}</Box>
          ) : connections.length === 0 ? (
            <>
              {platformSwitcher}
              <Typography color="text.secondary">
                <FormattedMessage
                  id="socialExport.connections.noneAssignedForStation"
                  defaultMessage="No hay cuentas conectadas asignadas a esta emisora para esta plataforma."
                />
              </Typography>
            </>
          ) : !hasAvailableSources ? (
            <>
              {platformSwitcher}
              <NoSourcesAlert
                description={publishMessages.panoramaNoSourcesComposition}
                linkTo={composeRouteGenerator.edits(composition.id)}
              />
            </>
          ) : (
            <SocialExportUploadLayout
              platform={platformSwitcher}
              media={
                showMediaSelector ? (
                  <MediaSelector
                    includeFragmentOption={false}
                    value={selectedSource}
                    onChange={onSelectedSourceChange}
                    edits={edits}
                  />
                ) : null
              }
              preview={effectiveMediaUrl ? <VideoPreview videoUrl={effectiveMediaUrl} /> : null}
              form={
                <>
                  {mediaLimitsIndication ? (
                    <MediaLimitsAlert indication={mediaLimitsIndication} />
                  ) : null}
                  {!canPublish && selectedSource
                    ? $t(publishMessages.mediaSelectorRenderNotReadyComposition)
                    : effectiveMediaUrl &&
                      !mediaLimitsIndication?.blocksPublish && (
                        <>
                          <PublishConnectionSelector
                            connections={connections}
                            selectedConnectionId={selectedConnectionId}
                            onSelectedConnectionChange={onSelectedConnectionChange}
                            platform={platform}
                          />
                          {areSettingsReady ? (
                            <PublishForm
                              key={`${platform.id}:${selectedConnectionId ?? ''}`}
                              source={publishSource}
                              videoUrl={effectiveMediaUrl}
                              onUpload={onUpload}
                              defaultSettings={settingsQuery.data}
                              connectionId={selectedConnectionId ?? ''}
                              actions={<SocialPublishFormActions platform={platform} />}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                              <CircularProgress />
                            </Box>
                          )}
                        </>
                      )}
                </>
              }
            />
          )}
        </Page.Content>
      </Page>
    </SocialExportContext.Provider>
  );
}
